import { baseApi } from './baseApi';
import { normalizeKeys } from '../../utils/transform';

export interface Webhook {
  webhookId: string;
  url: string;
  description: string | null;
  events: string[] | null;
  enabled: boolean;
  consecutiveFailures: number;
  lastDeliveryAt: string | null;
  signingSecret?: string;
}

export interface CreateWebhookRequest {
  url: string;
  description?: string;
  events?: string[];
  auth_headers?: Record<string, string>;
  enabled?: boolean;
}

export interface UpdateWebhookRequest {
  url?: string;
  description?: string;
  events?: string[];
  auth_headers?: Record<string, string>;
  enabled?: boolean;
  rotate_secret?: boolean;
}

export const webhooksApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getWebhooks: builder.query<Webhook[], string>({
      query: (appId) => `/portal/applications/${appId}/webhooks`,
      transformResponse: (response: unknown) => {
        const normalized = normalizeKeys<{ webhooks: Webhook[] }>(response);
        return normalized.webhooks ?? [];
      },
      providesTags: (_result, _error, appId) => [
        { type: 'Webhook' as const, id: appId },
      ],
    }),

    createWebhook: builder.mutation<
      Webhook,
      { appId: string; body: CreateWebhookRequest }
    >({
      query: ({ appId, body }) => ({
        url: `/portal/applications/${appId}/webhooks`,
        method: 'POST',
        body,
      }),
      transformResponse: (response: unknown) => normalizeKeys<Webhook>(response),
      invalidatesTags: (_r, _e, { appId }) => [
        { type: 'Webhook' as const, id: appId },
      ],
    }),

    updateWebhook: builder.mutation<
      Webhook,
      { appId: string; webhookId: string; body: UpdateWebhookRequest }
    >({
      query: ({ appId, webhookId, body }) => ({
        url: `/portal/applications/${appId}/webhooks/${webhookId}`,
        method: 'PATCH',
        body,
      }),
      transformResponse: (response: unknown) => normalizeKeys<Webhook>(response),
      invalidatesTags: (_r, _e, { appId }) => [
        { type: 'Webhook' as const, id: appId },
      ],
    }),

    deleteWebhook: builder.mutation<
      { deleted: boolean },
      { appId: string; webhookId: string }
    >({
      query: ({ appId, webhookId }) => ({
        url: `/portal/applications/${appId}/webhooks/${webhookId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_r, _e, { appId }) => [
        { type: 'Webhook' as const, id: appId },
      ],
    }),

    testWebhook: builder.mutation<
      { delivered: boolean },
      { appId: string; webhookId: string }
    >({
      query: ({ appId, webhookId }) => ({
        url: `/portal/applications/${appId}/webhooks/${webhookId}/test`,
        method: 'POST',
      }),
      transformResponse: (response: unknown) =>
        normalizeKeys<{ delivered: boolean }>(response),
    }),
  }),
});

export const {
  useGetWebhooksQuery,
  useCreateWebhookMutation,
  useUpdateWebhookMutation,
  useDeleteWebhookMutation,
  useTestWebhookMutation,
} = webhooksApi;
